import React, { useState, useEffect } from "react";
import { useBusiness, api } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";

const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];

// Generate placeholder data keys dynamically based on current year
const generateQuarterlyPlaceholder = () => {
  const currentYear = new Date().getFullYear();
  return {
    [`Q1-${currentYear}`]: {
      months: ["January", "February", "March"],
      total_revenue: 38200,
      total_profit: 5800,
      total_tax: 4300,
      total_owner_pay: 15000,
      total_operating_expense: 13100,
      breakdown: [
        { month: "Jan", revenue: 12500, expenses: 8200, profit: 4300 },
        { month: "Feb", revenue: 14200, expenses: 9100, profit: 5100 },
        { month: "Mar", revenue: 11500, expenses: 7800, profit: 3700 },
      ]
    },
    [`Q2-${currentYear}`]: {
      months: ["April", "May", "June"],
      total_revenue: 45600,
      total_profit: 8200,
      total_tax: 5100,
      total_owner_pay: 18000,
      total_operating_expense: 14300,
      breakdown: [
        { month: "Apr", revenue: 15600, expenses: 10200, profit: 5400 },
        { month: "May", revenue: 13400, expenses: 8900, profit: 4500 },
        { month: "Jun", revenue: 16600, expenses: 11500, profit: 5100 },
      ]
    }
  };
};

// Placeholder aggregated monthly data - would come from P&L + Bank
const PLACEHOLDER_QUARTERLY_DATA = generateQuarterlyPlaceholder();

const QuarterlyReview = () => {
  const { selectedBusiness } = useBusiness();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bankConnected, setBankConnected] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [formData, setFormData] = useState({
    quarter: `Q${Math.ceil((new Date().getMonth() + 1) / 3)}`,
    year: new Date().getFullYear(),
    total_revenue: "",
    total_profit: "",
    total_tax: "",
    total_owner_pay: "",
    total_operating_expense: "",
    profit_distribution: "",
    notes: "",
  });

  useEffect(() => {
    if (selectedBusiness) {
      fetchReviews();
      checkBankConnection();
    }
  }, [selectedBusiness]);

  const checkBankConnection = async () => {
    try {
      const response = await api.get("/plaid/accounts");
      setBankConnected(response.data.length > 0);
    } catch (error) {
      setBankConnected(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await api.get(`/quarterly-reviews/${selectedBusiness.id}`);
      setReviews(response.data);
    } catch (error) {
      console.error("Failed to fetch quarterly reviews:", error);
    } finally {
      setLoading(false);
    }
  };

  const getQuarterKey = () => `${formData.quarter}-${formData.year}`;
  const quarterData = showPlaceholder ? PLACEHOLDER_QUARTERLY_DATA[getQuarterKey()] : null;

  const importQuarterData = () => {
    if (quarterData) {
      setFormData(prev => ({
        ...prev,
        total_revenue: quarterData.total_revenue.toString(),
        total_profit: quarterData.total_profit.toString(),
        total_tax: quarterData.total_tax.toString(),
        total_owner_pay: quarterData.total_owner_pay.toString(),
        total_operating_expense: quarterData.total_operating_expense.toString(),
        profit_distribution: (quarterData.total_profit * 0.5).toFixed(2),
      }));
      toast.success("Quarterly data imported from bank records!");
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await api.post("/quarterly-reviews", {
        business_id: selectedBusiness.id,
        quarter: formData.quarter,
        year: formData.year,
        total_revenue: parseFloat(formData.total_revenue) || 0,
        total_profit: parseFloat(formData.total_profit) || 0,
        total_tax: parseFloat(formData.total_tax) || 0,
        total_owner_pay: parseFloat(formData.total_owner_pay) || 0,
        total_operating_expense: parseFloat(formData.total_operating_expense) || 0,
        profit_distribution: parseFloat(formData.profit_distribution) || 0,
        notes: formData.notes || null,
      });
      toast.success("Quarterly review saved!");
      fetchReviews();
      setFormData(prev => ({
        ...prev,
        total_revenue: "",
        total_profit: "",
        total_tax: "",
        total_owner_pay: "",
        total_operating_expense: "",
        profit_distribution: "",
        notes: "",
      }));
    } catch (error) {
      toast.error("Failed to save quarterly review");
    } finally {
      setSaving(false);
    }
  };

  if (!selectedBusiness) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Please select or create a business first</p>
      </div>
    );
  }

  const chartData = reviews.slice(0, 8).reverse().map(r => ({
    name: `${r.quarter} ${r.year}`,
    revenue: r.total_revenue,
    profit: r.total_profit,
    ownerPay: r.total_owner_pay,
    distribution: r.profit_distribution,
  }));

  const yearOptions = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= currentYear - 3; i--) {
    yearOptions.push(i);
  }

  const suggestedDistribution = formData.total_profit ? (parseFloat(formData.total_profit) * 0.5).toFixed(2) : "0.00";

  return (
    <div className="space-y-8" data-testid="quarterly-review-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-900">Quarterly Review</h1>
          <p className="text-stone-600">Track quarterly performance and profit distributions</p>
        </div>
        <div className="flex items-center gap-3">
          {!bankConnected && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
              <span className="mr-1">⚠️</span> Bank not connected
            </Badge>
          )}
          <Button
            variant="outline"
            onClick={() => setShowPlaceholder(!showPlaceholder)}
            className="text-sm"
          >
            {showPlaceholder ? "Hide" : "Show"} Sample Data
          </Button>
        </div>
      </div>

      {/* Bank Connection Banner */}
      {!bankConnected && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 21h18M3 10h18M5 6l7-3 7 3M4 10v11M20 10v11M8 14v3M12 14v3M16 14v3" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">Auto-Generate Quarterly Reports</h3>
                <p className="text-sm text-blue-700">
                  Connect your bank to automatically aggregate monthly data into quarterly summaries.
                  {showPlaceholder && " Sample data shown for demonstration."}
                </p>
              </div>
              <Button 
                onClick={() => window.location.href = '/bank-accounts'}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Connect Bank
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Chart */}
      {reviews.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Quarterly Performance</CardTitle>
            <CardDescription>Compare your financial metrics across quarters</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <XAxis dataKey="name" fontSize={12} />
                  <YAxis fontSize={12} tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                  <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill="#059669" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="profit" name="Profit" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="ownerPay" name="Owner Pay" fill="#34d399" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="distribution" name="Distribution" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* New Review Form */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>New Quarterly Review</CardTitle>
                <CardDescription>Enter your quarterly financial summary</CardDescription>
              </div>
              {showPlaceholder && quarterData && (
                <Button
                  onClick={importQuarterData}
                  variant="outline"
                  size="sm"
                  className="text-blue-600 border-blue-200 hover:bg-blue-50"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                    <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                  Import from Bank
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSave} className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Quarter</Label>
                  <Select value={formData.quarter} onValueChange={(value) => setFormData({ ...formData, quarter: value })}>
                    <SelectTrigger data-testid="quarter-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {QUARTERS.map(q => (
                        <SelectItem key={q} value={q}>{q}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select value={formData.year.toString()} onValueChange={(value) => setFormData({ ...formData, year: parseInt(value) })}>
                    <SelectTrigger data-testid="year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {yearOptions.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Monthly Breakdown Preview */}
              {showPlaceholder && quarterData && (
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-3">Monthly Breakdown (from Bank)</h4>
                  <div className="grid grid-cols-3 gap-3">
                    {quarterData.breakdown.map((m, i) => (
                      <div key={i} className="p-2 bg-white rounded border border-blue-100 text-center">
                        <p className="text-xs text-stone-500">{m.month}</p>
                        <p className="font-semibold text-[#1e3a5f]">${m.revenue.toLocaleString()}</p>
                        <p className="text-xs text-stone-400">Profit: ${m.profit.toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Total Revenue</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.total_revenue}
                      onChange={(e) => setFormData({ ...formData, total_revenue: e.target.value })}
                      className="pl-7"
                      placeholder="0.00"
                      data-testid="quarterly-revenue-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Total Profit (Accumulated)</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.total_profit}
                      onChange={(e) => setFormData({ ...formData, total_profit: e.target.value })}
                      className="pl-7"
                      placeholder="0.00"
                      data-testid="quarterly-profit-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Total Tax</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.total_tax}
                      onChange={(e) => setFormData({ ...formData, total_tax: e.target.value })}
                      className="pl-7"
                      placeholder="0.00"
                      data-testid="quarterly-tax-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Total Owner Pay</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.total_owner_pay}
                      onChange={(e) => setFormData({ ...formData, total_owner_pay: e.target.value })}
                      className="pl-7"
                      placeholder="0.00"
                      data-testid="quarterly-owner-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Total Operating Expense</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.total_operating_expense}
                      onChange={(e) => setFormData({ ...formData, total_operating_expense: e.target.value })}
                      className="pl-7"
                      placeholder="0.00"
                      data-testid="quarterly-opex-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Profit Distribution</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.profit_distribution}
                      onChange={(e) => setFormData({ ...formData, profit_distribution: e.target.value })}
                      className="pl-7"
                      placeholder={suggestedDistribution}
                      data-testid="quarterly-distribution-input"
                    />
                  </div>
                  <p className="text-xs text-stone-500">
                    Suggested (50% of profit): ${suggestedDistribution}
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Add any notes about this quarter..."
                  rows={3}
                  data-testid="quarterly-notes-input"
                />
              </div>

              <Button
                type="submit"
                disabled={saving}
                className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                data-testid="save-quarterly-btn"
              >
                {saving ? "Saving..." : "Save Quarterly Review"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Info & Past Reviews */}
        <div className="space-y-6">
          <Card className="bg-purple-50 border-purple-200">
            <CardHeader>
              <CardTitle className="text-purple-800">Quarterly Profit Distribution</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-purple-700">
                Each quarter, celebrate your success by taking <strong>50%</strong> of your accumulated Profit account as an owner distribution.
              </p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-purple-700 text-sm font-bold">1</span>
                  </div>
                  <p className="text-sm text-purple-700">
                    Check your Profit account balance
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-purple-700 text-sm font-bold">2</span>
                  </div>
                  <p className="text-sm text-purple-700">
                    Calculate 50% of the balance
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-purple-700 text-sm font-bold">3</span>
                  </div>
                  <p className="text-sm text-purple-700">
                    Transfer that amount as your owner distribution
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-purple-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-purple-700 text-sm font-bold">4</span>
                  </div>
                  <p className="text-sm text-purple-700">
                    Keep the other 50% for future growth
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Past Quarterly Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              {reviews.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {reviews.map(review => (
                    <div key={review.id} className="p-4 rounded-lg bg-stone-50 border border-stone-100">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-semibold text-stone-900">{review.quarter} {review.year}</span>
                        <span className="text-[#1e3a5f] font-bold">
                          ${review.total_revenue.toLocaleString()}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm text-stone-600">
                        <span>Profit: ${review.total_profit.toLocaleString()}</span>
                        <span>Distribution: ${review.profit_distribution.toLocaleString()}</span>
                      </div>
                      {review.notes && (
                        <p className="text-xs text-stone-500 mt-2 italic">{review.notes}</p>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-sm text-muted-foreground py-8">
                  No quarterly reviews yet. Create your first one!
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default QuarterlyReview;
